/**
 * Chatbot session token management.
 *
 * The chatbot API requires a short-lived HS256 JWT in the Authorization header,
 * minted by mxp-server-service via the `chatbotSession` GraphQL mutation. That
 * goes over /graphql, which already proxies to the gateway in every environment,
 * and GraphQLClient attaches the Okta bearer — so the gateway resolves a real
 * member identity. The chatbot is member-only; there is no anonymous token.
 *
 * Tokens are cached in memory and refreshed ~30s before expiry. authFetch wraps
 * `fetch` with token attachment and a single 401 retry.
 */
import { clientConfig, getPreloadedConfig } from '../../../config';
import { default as request } from 'utils/GraphQLClient';

// Resolved per call, not at module load: clientConfig is baked in at
// `react-scripts build`; the SSR preloaded state carries the runtime SSM values.
export const chatbotConfig = (): State.ChatbotConfig => getPreloadedConfig()?.chatbot ?? clientConfig.chatbot;

const REFRESH_BEFORE_EXPIRY_MS = 30000;

const CHATBOT_SESSION_MUTATION = `
  mutation ChatbotSession {
    chatbotSession {
      token
      expiresAt
    }
  }
`;

interface SessionToken {
  token: string;
  expiresAt: number;
}

let cached: SessionToken | null = null;
let inflight: Promise<SessionToken> | null = null;

async function fetchSession(): Promise<SessionToken> {
  const data = await request(CHATBOT_SESSION_MUTATION);
  const session = data?.chatbotSession;
  if (!session?.token || !session?.expiresAt) {
    throw new Error('Malformed chatbot session response');
  }
  return { token: session.token, expiresAt: session.expiresAt };
}

export async function getValidToken(): Promise<string> {
  if (!chatbotConfig().authRequired) return '';
  const now = Date.now();
  if (cached && cached.expiresAt - REFRESH_BEFORE_EXPIRY_MS > now) {
    return cached.token;
  }
  if (!inflight) {
    inflight = fetchSession()
      .then(session => {
        cached = session;
        return session;
      })
      .finally(() => {
        inflight = null;
      });
  }
  const session = await inflight;
  return session.token;
}

export function invalidateToken(): void {
  cached = null;
}

export async function authFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const doFetch = async (): Promise<Response> => {
    const token = await getValidToken();
    const headers = new Headers(init.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  };

  let res = await doFetch();
  if (res.status === 401 && chatbotConfig().authRequired) {
    invalidateToken();
    res = await doFetch();
  }
  return res;
}
